/**
 * /settings → Snapshots (admin-only).
 *
 * Toont alle workspace-snapshots, geeft de admin knoppen om er één te
 * maken of een bestaande terug te zetten. Bij restore wordt automatisch
 * eerst een "pre-restore"-snapshot gemaakt zodat de actie ongedaan kan
 * worden gemaakt door het pre-restore-blob terug te zetten.
 *
 * Snapshot-content (full JSON-dump) is niet zichtbaar in de UI — dat
 * zou de blob met potentieel duizenden concepten naar de browser
 * downloaden. Een "Inhoud bekijken"-knop opent de R2-blob in een
 * nieuw tabblad voor wie wil grasduinen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type FolderDTO,
  type OperationPreview,
  type SnapshotMetadata,
  type WorkPackageDTO,
} from "../api/client";
import { confirmDialog } from "../editor/confirmDialog";
import { OperationPreviewDialog } from "../editor/OperationPreviewDialog";
import { announceThenWait, finishOperation, impactFromPreview } from "../state/announceFirst";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";

interface Props {
  workspaceId: string;
}

/** Sentinel-value voor de scope-picker dropdown. Workspace-wide-snapshots
 *  zijn die zonder WP/Folder/Branch-prefix in hun label — typisch
 *  handmatig gemaakt via deze settings-tab. */
const SCOPE_WORKSPACE = "__workspace__";

export function SnapshotsSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const branchId = useSession((s) => s.branchId);
  const [snapshots, setSnapshots] = useState<SnapshotMetadata[]>([]);
  const [busy, setBusy] = useState(false);
  /** De droge run die aan de beheerder wordt voorgelegd, met de belofte die op
   *  z'n antwoord wacht. Zie `state/announceFirst.ts`. */
  const [previewVraag, setPreviewVraag] = useState<{
    preview: OperationPreview;
    label: string;
    beantwoord: (akkoord: boolean) => void;
  } | null>(null);

  const vraagOmAkkoord = (label: string) => (preview: OperationPreview) =>
    new Promise<boolean>((resolve) => {
      setPreviewVraag({
        preview,
        label,
        beantwoord: (akkoord) => {
          setPreviewVraag(null);
          resolve(akkoord);
        },
      });
    });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Reload-pending-flag — gezet na een succesvolle restore. Triggert
  // `window.location.reload()` bij component-unmount (= wanneer de
  // admin het Snapshots-venster sluit). Zo heeft de admin tijd om
  // de "Restore succesvol — XX changes"-toast in de dialog te lezen
  // vóór de pagina herlaadt; eerdere implementatie reloadde na 1.5s
  // en kapte de toast af.
  const reloadOnClose = useRef(false);
  useEffect(() => {
    return () => {
      if (reloadOnClose.current) {
        window.location.reload();
      }
    };
  }, []);

  // Scope-picker — workspace óf één van de workpackages. Default =
  // workspace zodat de admin direct ziet of z'n manuele "milestone"-
  // snapshots er staan zonder met WP-niveau-ruis te kampen.
  const [scope, setScope] = useState<string>(SCOPE_WORKSPACE);
  const [workpackages, setWorkpackages] = useState<WorkPackageDTO[]>([]);
  // Folder + branch-mapping per WP zodat we labels met
  // `Folder "<name>"` of `Branch "<name>"`-prefix kunnen koppelen aan
  // de juiste WP. We laden 't één keer bij mount.
  const [foldersByWp, setFoldersByWp] = useState<Map<string, FolderDTO[]>>(new Map());
  const [branchNamesByWp, setBranchNamesByWp] = useState<Map<string, string[]>>(new Map());

  // Form-state voor "nieuwe snapshot".
  const [draftLabel, setDraftLabel] = useState("");
  const [draftMessage, setDraftMessage] = useState("");

  // Workpackages + per-WP folders/branches éénmalig ophalen voor de
  // scope-picker en label-matching.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const wpRes = await api.listWorkpackages(workspaceId, /* includeClosed */ true);
        if (cancelled) return;
        setWorkpackages(wpRes.workpackages);
        // Folders zijn workspace-global maar hebben een workpackage_id;
        // bucket per WP.
        try {
          const foldersRes = await api.listFolders(workspaceId);
          if (cancelled) return;
          const byWp = new Map<string, FolderDTO[]>();
          for (const f of foldersRes.folders) {
            const arr = byWp.get(f.workpackageId) ?? [];
            arr.push(f);
            byWp.set(f.workpackageId, arr);
          }
          setFoldersByWp(byWp);
        } catch {
          // Niet-fatale fetch — alleen folder-snapshot-matching valt weg.
        }
        // Branches per WP (parallel, één call per WP — workspace heeft
        // typisch <20 WPs dus acceptabel).
        const branchEntries = await Promise.all(
          wpRes.workpackages.map(async (wp) => {
            try {
              const res = await api.listWorkpackageBranches(workspaceId, wp.id);
              return [wp.id, res.branches.map((b) => b.name)] as const;
            } catch {
              return [wp.id, [] as string[]] as const;
            }
          }),
        );
        if (cancelled) return;
        setBranchNamesByWp(new Map(branchEntries));
      } catch (err) {
        if (!cancelled) setError(explain(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const refresh = useCallback(async () => {
    if (!branchId) return;
    setError(null);
    try {
      // Altijd álle snapshots ophalen; filter is client-side via scope.
      const res = await api.listSnapshots(workspaceId, branchId, /* allBranches */ true);
      setSnapshots(res.items);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId, branchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Filter snapshots op basis van scope. Workspace-scope toont
  // snapshots zonder WP/Folder/Branch-prefix; WP-scope toont snapshots
  // waarvan het label past op een van de prefix-patronen van die WP.
  const filteredSnapshots = useMemo(() => {
    const matchesWpPrefix = (label: string, wpName: string) => label.startsWith(`WP "${wpName}"`);
    const isWpScoped = (label: string) =>
      label.startsWith('WP "') || label.startsWith('Folder "') || label.startsWith('Branch "');
    if (scope === SCOPE_WORKSPACE) {
      return snapshots.filter((s) => !isWpScoped(s.label));
    }
    const wp = workpackages.find((w) => w.id === scope);
    if (!wp) return [];
    const folderNames = (foldersByWp.get(wp.id) ?? []).map((f) => f.name);
    const branchNames = branchNamesByWp.get(wp.id) ?? [];
    return snapshots.filter((s) => {
      if (matchesWpPrefix(s.label, wp.name)) return true;
      if (folderNames.some((n) => s.label.startsWith(`Folder "${n}"`))) return true;
      if (branchNames.some((n) => s.label.startsWith(`Branch "${n}"`))) return true;
      return false;
    });
  }, [scope, snapshots, workpackages, foldersByWp, branchNamesByWp]);

  const createSnap = async () => {
    if (!branchId) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const labelTrim = draftLabel.trim();
      const messageTrim = draftMessage.trim();
      const meta = await api.createSnapshot(workspaceId, branchId, {
        ...(labelTrim ? { label: labelTrim } : {}),
        message: messageTrim || null,
      });
      setInfo(
        meta.reused
          ? t("settings.snapshots.reusedToast", { label: meta.label })
          : t("settings.snapshots.createdToast", {
              label: meta.label,
              concepts: meta.counts.concepts,
              relations: meta.counts.relations,
              diagrams: meta.counts.diagrams,
              viewStates: meta.counts.viewStates ?? 0,
            }),
      );
      setDraftLabel("");
      setDraftMessage("");
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const restoreSnap = async (snap: SnapshotMetadata) => {
    if (!branchId) return;
    const vraag =
      snap.scope_kind === "workpackage_branch"
        ? "settings.snapshots.restoreConfirmBranch"
        : "settings.snapshots.restoreConfirm";
    if (!(await confirmDialog({ message: t(vraag, { label: snap.label }) }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Eerst aankondigen; de server leidt uit de snapshot af hoe breed dit
      // gaat — een workspace-brede restore raakt élk workpackage. Zie
      // `docs/architecture/gelijktijdigheid.md` §7.
      const preview = await api.dryRunRestore(workspaceId, branchId, snap.id);
      // Een branch-snapshot zet ook de plateaus eronder terug: nieuwere gaan
      // weg, verdwenen komen terug. Dat hoort de admin vooraf te weten.
      const structuur = preview.branch_check?.structure;
      if (structuur && (structuur.remove.length > 0 || structuur.recreate.length > 0)) {
        const regels = [
          ...structuur.remove.map((b) => t("settings.snapshots.branchRemoved", { name: b.name })),
          ...structuur.recreate.map((b) =>
            t("settings.snapshots.branchRecreated", { name: b.name }),
          ),
        ];
        if (
          !(await confirmDialog({
            message: `${t("settings.snapshots.structureIntro")}\n\n${regels.join("\n")}`,
          }))
        )
          return;
      }
      const uitkomst = await announceThenWait(workspaceId, branchId, {
        kind: "restore",
        label: snap.label,
        snapshotId: snap.id,
        impact: impactFromPreview(preview),
        preview,
        askPreview: vraagOmAkkoord(snap.label),
      });
      if (!uitkomst.go) {
        setInfo(t("settings.snapshots.restoreCancelled"));
        return;
      }
      let res: Awaited<ReturnType<typeof api.restoreSnapshot>>;
      try {
        res = await api.restoreSnapshot(workspaceId, branchId, snap.id);
      } finally {
        await finishOperation(workspaceId, branchId, uitkomst.operationId);
      }
      const s = res.summary;
      const toastBody = t("settings.snapshots.restoredToast", {
        label: snap.label,
        ci: s.concepts_inserted,
        cu: s.concepts_updated,
        // ct = tombstone-on-feature-branch + hard-delete-on-main combined,
        // zodat de toast de TOTALE "weg na restore"-telling toont,
        // ongeacht main/feature. Fix 2026-05-24: voorheen toonde 'ie
        // alleen tombstones en miste hard-deletes.
        ct: s.concepts_tombstoned + (s.concepts_deleted ?? 0),
        ri: s.relations_inserted,
        ru: s.relations_updated,
        rt: s.relations_tombstoned + (s.relations_deleted ?? 0),
        dr: s.diagrams_replaced,
        dx: s.diagrams_removed,
        safety: res.safety_snapshot.label,
      });
      // Voeg hint toe over de aanstaande reload, los van de bestaande
      // i18n-key zodat NL en EN beide werken zonder een nieuwe vertaling.
      setInfo(`${toastBody}\n\n${t("settings.snapshots.reloadHint")}`);
      // Lokale store herhydrateren voor de actieve branch. Helaas
      // niet voldoende voor alle panelen — de Foldertree (in
      // Base-werkpakket) en het Diagrammen-paneel hebben hun eigen
      // data-fetches die niet altijd luisteren naar de
      // workspaceData-revision-bump. Snapshot-restore is een
      // werkruimte-wide event; we doen daarom een full page-reload,
      // maar PAS wanneer de admin het Snapshots-venster sluit
      // (zie de useEffect bovenaan met reloadOnClose-ref). Zo heeft
      // de admin tijd om de restore-toast + counts in de dialog te
      // lezen.
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      await refresh();
      reloadOnClose.current = true;
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteSnap = async (snap: SnapshotMetadata) => {
    if (
      !(await confirmDialog({
        message: t("settings.snapshots.deleteConfirm", { label: snap.label }),
        danger: true,
      }))
    )
      return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (!branchId) return;
      await api.deleteSnapshot(workspaceId, branchId, snap.id);
      setInfo(t("settings.snapshots.deletedToast", { label: snap.label }));
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      {/* Wat het terugzetten gaat doen — vooral: wat er verdwijnt. */}
      {previewVraag && (
        <OperationPreviewDialog
          preview={previewVraag.preview}
          label={previewVraag.label}
          onConfirm={() => previewVraag.beantwoord(true)}
          onCancel={() => previewVraag.beantwoord(false)}
        />
      )}
      <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-text) text-sm">
        <p className="mb-1">
          <strong>{t("settings.snapshots.introBoldTitle")}</strong>
          {t("settings.snapshots.introAfter")}
        </p>
        <p className="text-(--ld-muted) text-xs">{t("settings.snapshots.introDetail")}</p>
      </div>

      <section className="rounded border border-(--ld-border) bg-(--ld-surface) px-4 py-3">
        <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
          {t("settings.snapshots.newSectionTitle")}
        </h3>
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-muted)">{t("settings.snapshots.labelLabel")}</span>
            <input
              type="text"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder={t("settings.snapshots.labelPlaceholder", {
                date: new Date().toLocaleDateString(),
              })}
              className="rounded border border-(--ld-control) px-3 py-1.5 text-sm focus:border-(--ld-accent) focus:outline-none"
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-muted)">{t("settings.snapshots.messageLabel")}</span>
            <textarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder={t("settings.snapshots.messagePlaceholder")}
              className="min-h-[60px] rounded border border-(--ld-control) px-3 py-1.5 text-sm focus:border-(--ld-accent) focus:outline-none"
              disabled={busy}
              rows={2}
            />
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void createSnap()}
              disabled={busy || !branchId}
              className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            >
              {t("settings.snapshots.createButton")}
            </button>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-(--ld-muted) text-sm">
          <span>{t("settings.snapshots.scopeLabel")}</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
            disabled={busy}
          >
            <option value={SCOPE_WORKSPACE}>{t("settings.snapshots.scopeWorkspace")}</option>
            {workpackages.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.name}
                {wp.isBase ? ` (${t("settings.snapshots.scopeBaseMarker")})` : ""}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy}
          className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
        >
          {t("settings.common.refresh")}
        </button>
      </div>

      {error && (
        <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      {filteredSnapshots.length === 0 ? (
        <p className="rounded border border-dashed border-(--ld-control) bg-(--ld-surface) px-3 py-6 text-center text-(--ld-muted) text-sm italic">
          {t("settings.snapshots.empty")}
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-(--ld-border) border-b text-left text-(--ld-muted)">
              <th className="py-1.5 pr-3">{t("settings.snapshots.colLabel")}</th>
              <th className="py-1.5 pr-3">{t("settings.snapshots.colWhen")}</th>
              <th className="py-1.5 pr-3">{t("settings.snapshots.colWho")}</th>
              <th className="py-1.5 pr-3">{t("settings.snapshots.colContent")}</th>
              <th className="py-1.5 pr-3">{t("settings.snapshots.colSize")}</th>
              <th className="py-1.5 pr-3">{t("settings.snapshots.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSnapshots.map((s) => (
              <tr key={s.id} className="border-(--ld-rule) border-b align-top">
                <td className="py-1.5 pr-3">
                  <div className="font-medium text-(--ld-text)">{s.label}</div>
                  {s.message && (
                    <div className="mt-0.5 text-(--ld-muted) text-xs italic">{s.message}</div>
                  )}
                  {s.restorable && s.restorable.status !== "yes" && (
                    <div
                      className={`mt-0.5 text-xs ${s.restorable.status === "never" ? "text-(--ld-negative)" : "text-(--ld-notice-ink)"}`}
                    >
                      {t(
                        s.restorable.status === "never"
                          ? "settings.snapshots.statusNever"
                          : "settings.snapshots.statusLater",
                      )}
                      {s.restorable.reason ? ` ${s.restorable.reason}` : ""}
                    </div>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs tabular-nums">
                  {formatTimestamp(s.created_at)}
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs tabular-nums">
                  {s.created_by}
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs">
                  {s.counts.concepts}c · {s.counts.relations}r · {s.counts.diagrams}d
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs">
                  {formatBytes(s.byte_size)}
                </td>
                <td className="py-1.5 pr-3">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => void restoreSnap(s)}
                      disabled={
                        busy || s.restorable?.status === "later" || s.restorable?.status === "never"
                      }
                      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-medium text-(--ld-text) text-xs hover:bg-(--ld-raised) disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)"
                      title={t("settings.snapshots.restoreTitle")}
                    >
                      {t("settings.snapshots.restoreButton")}
                    </button>
                    <a
                      href={`/api/v1/workspaces/${workspaceId}/snapshots/${s.id}/content`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-medium text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                      title={t("settings.snapshots.jsonTitle")}
                    >
                      {t("settings.snapshots.jsonButton")}
                    </a>
                    <button
                      type="button"
                      onClick={() => void deleteSnap(s)}
                      disabled={busy}
                      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-medium text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-50"
                      title={t("settings.snapshots.deleteTitle")}
                    >
                      {t("settings.snapshots.deleteButton")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function formatTimestamp(ts: string): string {
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return ts;
  return new Date(ms).toLocaleString();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
